import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo } from 'react';
import ReactGA from 'react-ga4';
import Logger from '../infrastructure/Logging/Logger';
import { useFeatureFlags } from '../application/TenantsSlice/useFeatureFlags';
import globalFeatureFlags from '../globalFeaturesFlags';
import { GoogleAnalyticsSettingRepository } from '../infrastructure/GoogleAnalyticsSlice/GoogleAnalyticsSettingRepository';

import { GoogleAnalyticsSetting } from '../domain/GoogleAnalyticsSlice/GoogleAnalyticsSetting';
import { StoreStatus, StoreStatusType } from './StoreStatus';
import { initialStoreStateBase, StoreStateBase } from './StoreStateBase';
import type { RootState, AppDispatch } from './index';
import { useLocation } from 'react-router-dom';

// State definition
export interface GoogleAnalyticsState extends StoreStateBase {
  entity: GoogleAnalyticsSetting;
}

const initialState: GoogleAnalyticsState = {
  ...initialStoreStateBase,
  entity: defaultGA(),
};

function defaultGA(): GoogleAnalyticsSetting {
  return { googleAnalyticsSettingId: 0, measurementId: "" };
}

// Slice without direct repo access; external code sets entity
const googleAnalyticsSlice = createSlice({
  name: 'googleAnalytics',
  initialState,
  reducers: {
    setGoogleAnalyticsSetting(state, action: PayloadAction<GoogleAnalyticsSetting | null>) {
      state.entity = (action.payload ?? defaultGA());
      state.storeStatus = StoreStatus.succeeded as StoreStatusType;
    },
    setGoogleAnalyticsLoading(state) {
      state.storeStatus = StoreStatus.loading as StoreStatusType;
      state.error = null;
    },
    setGoogleAnalyticsError(state, action: PayloadAction<string | null>) {
      state.storeStatus = StoreStatus.failed as StoreStatusType;
      state.error = action.payload ?? 'Error GA';
    },
  },
});

export const { setGoogleAnalyticsSetting, setGoogleAnalyticsLoading, setGoogleAnalyticsError } = googleAnalyticsSlice.actions;
export const googleAnalyticsReducer = googleAnalyticsSlice.reducer;

// Selectors
export const selectGoogleAnalyticsSetting = (state: RootState) => state.googleAnalytics.entity;
export const selectGoogleAnalyticsStatus = (state: RootState) => state.googleAnalytics?.storeStatus ?? StoreStatus.idle;

// Hook facade
export function useGoogleAnalytics() {
  // All hooks called at the top level
  const dispatch = useDispatch<AppDispatch>();
  const gaSetting = useSelector(selectGoogleAnalyticsSetting);
  const status = useSelector(selectGoogleAnalyticsStatus);
  const { isTenantFeatureEnabled } = useFeatureFlags();

  // Memoize feature flag and measurementId
  const gaFeatureFlagEnabled = useMemo(() => isTenantFeatureEnabled(globalFeatureFlags.GOOGLE_ANALYTICS), [isTenantFeatureEnabled]);
  const hasMeasurementId = useMemo(() => !!gaSetting?.measurementId, [gaSetting]);

  // Shared ref across hook invocations (store in globalThis to avoid reinitialization in multi-render scenarios)
  const isFetchingRef: { current: boolean } = (globalThis as any).__ga_fetching_ref || { current: false };
  (globalThis as any).__ga_fetching_ref = isFetchingRef;

  const setEntity = useCallback((entity: GoogleAnalyticsSetting | null) => {
    dispatch(setGoogleAnalyticsSetting(entity));
  }, [dispatch]);

  const setLoading = useCallback(() => dispatch(setGoogleAnalyticsLoading()), [dispatch]);
  const setError = useCallback((msg: string | null) => dispatch(setGoogleAnalyticsError(msg)), [dispatch]);

  // Lightweight public fetch (anon allowed) if setting absent
  const ensureFetched = useCallback(async (): Promise<GoogleAnalyticsSetting | null> => {
    if (gaSetting || isFetchingRef.current) return gaSetting ?? null;
    isFetchingRef.current = true;
    setLoading();
    try {
      const repo = new GoogleAnalyticsSettingRepository();
      const setting = await repo.getOne();
      setEntity(setting ?? null);
      return setting ?? null;
    } catch (e: any) {
      setError(e?.message ?? 'GA fetch failed');
      Logger.error(`GA ensureFetched error: ${e?.message ?? 'unknown_error'}`);
      return null;
    } finally {
      isFetchingRef.current = false;
    }
  }, [gaSetting, setEntity, setLoading, setError]);

  // Async initialize that first ensures fetch, then initializes GA once
  const initialize = useCallback(async () => {
    if (!gaFeatureFlagEnabled) {
      Logger.event('GA_initialize_skipped', { reason: 'feature_disabled' });
      return;
    }
    const setting = await ensureFetched();
    const id = setting?.measurementId?.trim();
    if (!id) {
      Logger.event('GA_initialize_skipped', { reason: 'missing_measurement_id' });
      return;
    }
    try {
      if (!(window as any).__ga4_initialized) {
        ReactGA.initialize(id);
        (window as any).__ga4_initialized = true;
        Logger.event('GA_initialized', { measurementId: id });
      }
    } catch (e: any) {
      Logger.error(`GA initialize error: ${e?.message ?? 'unknown_error'}`);
    }
  }, [ensureFetched, gaFeatureFlagEnabled]);

  const sendPageView = useCallback((pageUri: string) => {
    try {
      if (!gaFeatureFlagEnabled) {
        Logger.event('GA_pageview_logged', { page: pageUri, mode: 'logger_only' });
        return;
      }
      if (!hasMeasurementId) {
        Logger.event('GA_pageview_skipped', { page: pageUri, reason: 'missing_measurement_id' });
        return;
      }
      ReactGA.send({ hitType: 'pageview', page: pageUri });
    } catch (e: any) {
      Logger.error(`GA pageview error: ${e?.message ?? 'unknown_error'}`);
    }
  }, [gaFeatureFlagEnabled, hasMeasurementId]);

  return {
    gaSetting,
    status,
    initialize,
    sendPageView,
    setEntity,
    setLoading,
    setError,
  } as const;
}

// Simple event tracker hook
const useAnalyticsEventTracker = (category: string = 'General') => {
  // All hooks at the top level
  const { isTenantFeatureEnabled } = useFeatureFlags();
  const gaFeatureFlagEnabled = useMemo(() => isTenantFeatureEnabled(globalFeatureFlags.GOOGLE_ANALYTICS), [isTenantFeatureEnabled]);
  const setting = useSelector(selectGoogleAnalyticsSetting);
  const hasMeasurementId = useMemo(() => !!setting?.measurementId, [setting]);

  // Memoize the event tracker function
  const eventTracker = useCallback((action: string = 'button_click', label: string = 'button') => {
    try {
      if (!gaFeatureFlagEnabled) {
        Logger.event('GA_event_logged', { category, action, label, mode: 'logger_only' });
        return;
      }
      if (!hasMeasurementId) {
        Logger.event('GA_event_skipped', { category, action, label, reason: 'missing_measurement_id' });
        return;
      }
      ReactGA.event({ category, action, label });
    } catch (e: any) {
      Logger.error(`GA event error: ${e?.message ?? 'unknown_error'}`);
    }
  }, [category, gaFeatureFlagEnabled, hasMeasurementId]);

  return eventTracker;
};

export default useAnalyticsEventTracker;

export const GoogleAnalyticsBootstrap: React.FC = () => {
  const { initialize, sendPageView } = useGoogleAnalytics();
  const location = useLocation();
  useEffect(() => {
    (async () => {
      await initialize(); // ensures fetch & GA init
      sendPageView(location.pathname + location.search);
    })();
  }, [location.pathname, location.search, initialize, sendPageView]);
  return null;
};